import React, { useEffect, useRef } from "react";
import { useTheme } from "../theme-provider";

const AnimatedBackground = () => {
  const canvasRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let animationFrameId;
    let particles = [];
    
    // Configuration
    const particleCount = 100;
    const connectionDistance = 150;
    const mouseDistance = 180;

    // Color Palettes
    const palettes = {
      light: [
        "rgba(79, 70, 229, 0.4)",  // Indigo
        "rgba(59, 130, 246, 0.4)",  // Blue
        "rgba(139, 92, 246, 0.4)",  // Purple
      ],
      dark: [
        "rgba(34, 211, 238, 0.9)",  // Neon Cyan
        "rgba(167, 139, 250, 0.9)",  // Neon Purple
        "rgba(255, 255, 255, 0.8)",  // Bright White
      ]
    };

    // Resize handler
    const setSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setSize();
    window.addEventListener("resize", setSize);

    // Mouse tracking
    const mouse = { x: null, y: null };
    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", () => {
      mouse.x = null;
      mouse.y = null;
    });

    // Particle Class
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.15;
        this.vy = (Math.random() - 0.5) * 0.15;
        this.size = Math.random() * 3 + 3;
        this.colorIndex = Math.floor(Math.random() * 3);
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Bounce off edges
        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        // Mouse interaction
        if (mouse.x != null) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < mouseDistance) {
            const forceDirectionX = dx / distance;
            const forceDirectionY = dy / distance;
            const force = (mouseDistance - distance) / mouseDistance;
            const directionX = forceDirectionX * force * 1.5;
            const directionY = forceDirectionY * force * 1.5;

            this.vx -= directionX * 0.01;
            this.vy -= directionY * 0.01;
          }
        }
      }

      draw(isDark) {
        const colors = isDark ? palettes.dark : palettes.light;
        const color = colors[this.colorIndex];

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = color;
        
        if (isDark) {
          ctx.shadowBlur = 20; // Increased glow
          ctx.shadowColor = color;
        } else {
          ctx.shadowBlur = 0;
        }
        
        ctx.fill();
        ctx.shadowBlur = 0; // Reset for performance
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
    }

    // Animation Loop
    const animate = () => {
      if (!ctx || !canvas) return;
      
      const isDark = 
        theme === 'dark' || 
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) ||
        document.documentElement.classList.contains('dark');

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and Draw Particles
      particles.forEach((particle) => {
        particle.update();
        particle.draw(isDark);
      });

      // Draw Connections
      connectParticles(isDark);

      animationFrameId = requestAnimationFrame(animate);
    };

    const connectParticles = (isDark) => {
      if (!ctx) return;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.3;
            const baseColor = isDark ? "rgba(255, 255, 255," : "rgba(0, 0, 0,";
            
            ctx.strokeStyle = baseColor + opacity + ")";
            ctx.lineWidth = isDark ? 1.5 : 1; // Thicker lines in dark mode
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
        
        // Connect to mouse
        if (mouse.x != null) {
            const dx = particles[i].x - mouse.x;
            const dy = particles[i].y - mouse.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < mouseDistance) {
                const opacity = (1 - distance / mouseDistance) * 0.5;
                ctx.strokeStyle = `rgba(${isDark ? "129, 140, 248" : "79, 70, 229"}, ${opacity})`;
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.moveTo(particles[i].x, particles[i].y);
                ctx.lineTo(mouse.x, mouse.y);
                ctx.stroke();
            }
        }
      }
    };

    // Initial size and start animation
    setSize();
    animate();

    return () => {
      window.removeEventListener("resize", setSize);
      window.removeEventListener("mousemove", handleMouseMove);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full z-[-1] pointer-events-none"
    />
  );
};

export default AnimatedBackground;